import React, { useState, useEffect } from 'react'
import { OrderManagementService } from '@joinomu/shared'
import type { OrderAuditLog, MedicationOrder } from '@joinomu/shared'

interface OrderAuditTrailProps {
  orderId: string
  className?: string
}

interface AuditLogWithUser extends OrderAuditLog {
  user_email?: string
  user_name?: string
}

export function OrderAuditTrail({ orderId, className = '' }: OrderAuditTrailProps) {
  const [auditLogs, setAuditLogs] = useState<AuditLogWithUser[]>([])
  const [order, setOrder] = useState<MedicationOrder | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAuditTrail()
  }, [orderId])

  const loadAuditTrail = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load order details and audit log
      const [orderResult, auditResult] = await Promise.all([
        OrderManagementService.getOrder(orderId),
        OrderManagementService.getOrderAuditLog(orderId)
      ])

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to load order')
      }

      if (!auditResult.success) {
        throw new Error(auditResult.error || 'Failed to load audit log')
      }

      setOrder(orderResult.data)
      
      // Enrich audit logs with user information if needed
      const enrichedLogs = auditResult.data || []
      setAuditLogs(enrichedLogs)

    } catch (err) {
      console.error('❌ Error loading order audit trail:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const formatFieldName = (fieldName: string) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatValue = (value: string | null) => {
    if (value === null || value === 'null') return 'None'
    if (value === '') return 'Empty'
    
    // Format specific field types
    if (value.includes('T') && value.includes(':')) {
      try {
        return new Date(value).toLocaleString()
      } catch {
        return value
      }
    }
    
    return value
  }

  const getChangeTypeColor = (fieldName: string, oldValue: string | null, newValue: string | null) => {
    if (fieldName === 'order_created') return 'text-green-600 bg-green-50'
    if (oldValue === null) return 'text-blue-600 bg-blue-50'
    if (newValue === null) return 'text-red-600 bg-red-50'
    return 'text-yellow-600 bg-yellow-50'
  }

  const getChangeIcon = (fieldName: string, oldValue: string | null, newValue: string | null) => {
    if (fieldName === 'order_created') return '🆕'
    if (oldValue === null) return '➕'
    if (newValue === null) return '➖'
    return '🔄'
  }

  if (loading) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading Audit Trail</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <button
            onClick={loadAuditTrail}
            className="mt-3 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Order Modification History</h3>
        <button
          onClick={loadAuditTrail}
          className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
          title="Refresh audit trail"
        >
          🔄 Refresh
        </button>
      </div>

      {order && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Order Details</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Order ID:</span> {order.id}</div>
            <div><span className="font-medium">Status:</span> {order.fulfillment_status}</div>
            <div><span className="font-medium">Payment:</span> {order.payment_status}</div>
            <div><span className="font-medium">Total:</span> ${order.total_amount}</div>
          </div>
        </div>
      )}

      {auditLogs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No modification history available for this order.</p>
          <p className="text-sm mt-1">Changes will appear here when the order is modified.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">
                      {getChangeIcon(log.field_name, log.old_value, log.new_value)}
                    </span>
                    <span className="font-medium text-gray-900">
                      {formatFieldName(log.field_name)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getChangeTypeColor(log.field_name, log.old_value, log.new_value)}`}>
                      {log.field_name === 'order_created' ? 'Created' : 'Modified'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    {log.field_name !== 'order_created' && (
                      <>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Previous Value:</span>
                          <div className="text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded mt-1">
                            {formatValue(log.old_value)}
                          </div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">New Value:</span>
                          <div className="text-sm text-gray-900 bg-blue-50 px-2 py-1 rounded mt-1">
                            {formatValue(log.new_value)}
                          </div>
                        </div>
                      </>
                    )}
                    {log.field_name === 'order_created' && (
                      <div className="col-span-2">
                        <span className="text-sm font-medium text-gray-500">Order Created:</span>
                        <div className="text-sm text-gray-900 bg-green-50 px-2 py-1 rounded mt-1">
                          New order created from medication approval
                        </div>
                      </div>
                    )}
                  </div>

                  {log.change_reason && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-gray-500">Reason:</span>
                      <div className="text-sm text-gray-700 italic mt-1">
                        "{log.change_reason}"
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-right text-sm text-gray-500 ml-4">
                  <div>{new Date(log.created_at).toLocaleDateString()}</div>
                  <div>{new Date(log.created_at).toLocaleTimeString()}</div>
                  {log.user_name && (
                    <div className="text-xs mt-1">by {log.user_name}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}